import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { AppButton } from '@/components/AppButton'
import { CardItem } from '@/components/CardItem'

import { useRequestItemHandler } from '@/hooks/useRequestItemHandler'
import { Request } from '@/domain/requests'
import { CurrencyCatalogsDTO } from '@/domain/currencies'
import { formatFiatCurrency } from '@/utils/currency/formatFiatCurrency'

interface RequestItemProps {
  request: Request
  onDecline?: () => void
  onAccept?: () => void
  style?: React.CSSProperties
  isLoading?: boolean
  width?: number | string
  showFullNote?: boolean
  isHorizontalList?: boolean
  displayCurrency?: CurrencyCatalogsDTO
}

export const RequestItem: React.FC<RequestItemProps> = ({
  request,
  onDecline,
  onAccept,
  style,
  isLoading = false,
  width,
  showFullNote = false,
  isHorizontalList: _isHorizontalList = false,
  displayCurrency,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [wasCanceled, setWasCanceled] = useState(false)

  // Use the same handler as request-details page
  const {
    formattedRequest,
    handleCancel,
    handleNext,
    isLoadingCancel,
    isLoadingNext,
  } = useRequestItemHandler({
    request,
    onCancelSuccess: onDecline,
  })

  const { note } = request

  // Use formatted data from handler
  const {
    userFullName,
    avatar,
    title,
    cancelLabel,
    nextLabel,
    isPaymentLink,
    isPending,
  } = formattedRequest

  const handlePress = () => {
    navigate(`/request-details/${request.id}`, {
      state: { request },
    })
  }

  const formatDate = (date: Date) => {
    const now = Date.now()
    const diffInHours = Math.floor((now - date.getTime()) / (1000 * 60 * 60))

    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours}h ago`

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
    })
  }

  // Determine avatar props
  const shouldRenderGradientBackground =
    !('uri' in avatar) || (!avatar.uri && !!avatar.colors)
  const avatarProps = shouldRenderGradientBackground
    ? {
        name: userFullName,
        colors: ('colors' in avatar && avatar.colors) || ['#6366F1', '#8B5CF6'],
      }
    : {
        uri: ('uri' in avatar && avatar.uri) || undefined,
        name: userFullName,
      }

  // Action handlers with event prevention
  const handleDeclineClick = async (e?: React.MouseEvent) => {
    e?.stopPropagation()
    await handleCancel()
      .then(() => setWasCanceled(true))
      .catch(() => setWasCanceled(false))
  }

  const handleAcceptClick = async (e?: React.MouseEvent) => {
    e?.stopPropagation()
    await handleNext()
    onAccept?.()
  }

  // Combine loading states
  const isButtonLoading = isLoading || isLoadingCancel || isLoadingNext

  const amountFormatted = formatFiatCurrency({
    value: Number(request.amount.fiat_amount),
    showDecimals: true,
    fiatCode: displayCurrency?.isin || request.amount.fiat_currency,
  })

  return (
    <CardItem.Root
      onPress={handlePress}
      style={{
        width,
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        minHeight: '200px',
        transition: 'all 0.2s ease',
        opacity: wasCanceled ? 0.5 : 1,
        pointerEvents: wasCanceled ? 'none' : 'auto',
        cursor: wasCanceled ? 'default' : 'pointer',
        ...style,
      }}
    >
      {/* Top content */}
      <BaseView style={{ gap: '16px', width: '100%' }}>
        {/* Header with avatar and user info */}
        <BaseView row gap={8} align="center">
          {shouldRenderGradientBackground ? (
            <CardItem.AvatarGradient
              colors={avatarProps.colors || ['#6366F1', '#8B5CF6']}
              name={avatarProps.name}
              iconName="users"
              start={[1, 0]}
              end={[0, 1]}
            />
          ) : (
            <CardItem.Avatar
              uri={avatarProps.uri}
              name={avatarProps.name}
              size={48}
            />
          )}

          <BaseView>
            <CardItem.Title title={userFullName} />
            {request.created_at && (
              <CardItem.Description
                description={formatDate(request.created_at)}
              />
            )}
          </BaseView>
        </BaseView>

        {/* Request details */}
        <BaseView row justify="space-between" style={{ width: '100%' }}>
          <BaseView
            row
            align="flex-start"
            gap={8}
            style={{ flex: 1, justifyContent: 'flex-start' }}
          >
            <BaseText
              fontSize={16}
              fontFamily="semibold"
              color="label.default"
              style={{ flex: 1 }}
            >
              {title}
            </BaseText>
          </BaseView>

          <BaseView gap={3}>
            <BaseText fontFamily="semibold" fontSize={18}>
              {request.amountFormatted}
            </BaseText>
            {request.relativeFiatAmountFormatted && (
              <BaseText
                color="label.description"
                fontSize={16}
                fontFamily="medium"
              >
                {amountFormatted}
              </BaseText>
            )}
          </BaseView>
        </BaseView>

        {/* Note */}
        {note && (
          <BaseText
            fontSize={16}
            fontFamily="medium"
            color="label.description"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: showFullNote ? 'block' : '-webkit-box',
              WebkitLineClamp: showFullNote ? 'none' : 1,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {note}
          </BaseText>
        )}
      </BaseView>

      {/* Action buttons */}
      <BaseView
        row
        align="center"
        gap={8}
        style={{ width: '100%', paddingTop: '18px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {isPaymentLink ? (
          isPending ? (
            <AppButton
              size="small"
              style={{
                borderRadius: 32,
                fontSize: 18,
              }}
              height={44}
              onPress={handleDeclineClick}
              type="secondaryGray"
              label={cancelLabel}
              isLoading={isLoadingCancel || isLoading}
              disabled={isButtonLoading}
            />
          ) : (
            <BaseView
              py={8}
              px={16}
              rounded={20}
              style={{
                backgroundColor:
                  request.state === 'accepted'
                    ? 'rgba(52, 199, 89, 0.15)'
                    : 'rgba(255, 69, 58, 0.15)',
              }}
            >
              <BaseText
                fontSize={14}
                fontFamily="medium"
                style={{
                  color:
                    request.state === 'accepted' ? '#34C759' : '#FF453A',
                }}
              >
                {request.state === 'accepted'
                  ? t('translation:claimed', 'Claimed')
                  : t('translation:cancelled', 'Cancelled')}
              </BaseText>
            </BaseView>
          )
        ) : (
          <>
            <AppButton
              size="small"
              style={{
                borderRadius: 32,
                fontSize: 18,
              }}
              height={44}
              onPress={handleDeclineClick}
              type="secondaryGray"
              isSharedSpace
              label={cancelLabel}
              isLoading={isLoadingCancel || isLoading}
              disabled={isButtonLoading}
            />

            <AppButton
              size="small"
              style={{
                borderRadius: 32,
                fontSize: 18,
              }}
              height={44}
              onPress={handleAcceptClick}
              type="primaryGradient"
              isSharedSpace
              label={nextLabel}
              isLoading={isLoadingNext || isLoading}
              disabled={isButtonLoading}
            />
          </>
        )}
      </BaseView>
    </CardItem.Root>
  )
}
